import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clearOutbox } from "./outbox";
import { getExpiresAt, logout as doLogout, onAuthChange, refreshSession, startLogin, type AuthProviderName } from "./auth";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthValue {
  status: AuthStatus;
  login: (provider: AuthProviderName, returnTo: string) => void;
  logout: () => Promise<boolean>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const qc = useQueryClient();
  const timer = useRef<number | undefined>(undefined);

  const schedule = useCallback(() => {
    window.clearTimeout(timer.current);
    const wait = Math.max(5_000, getExpiresAt() - Date.now() - 60_000);
    timer.current = window.setTimeout(() => void refreshSession(), wait);
  }, []);

  useEffect(() => {
    let alive = true;
    const off = onAuthChange((token) => {
      if (!alive) return;
      if (token) {
        setStatus("authenticated");
        schedule();
      } else {
        window.clearTimeout(timer.current);
        setStatus("anonymous");
      }
    });
    void refreshSession().then((r) => {
      // ถ้าเครือข่ายล้มเหลว ถือว่ายังไม่ได้เข้าสู่ระบบ ผู้ใช้ยังถ่ายรูปได้ตามปกติ
      if (alive && r.status !== "ok") setStatus("anonymous");
    });
    const onOnline = () => void refreshSession();
    window.addEventListener("online", onOnline);
    return () => {
      alive = false;
      off();
      window.clearTimeout(timer.current);
      window.removeEventListener("online", onOnline);
    };
  }, [schedule]);

  const logout = useCallback(async () => {
    const ok = await doLogout();
    if (ok) {
      qc.removeQueries({ queryKey: ["me"] });
      // คำสั่งที่ค้างในคิวเป็นของบัญชีเดิม ไม่ส่งต่อให้บัญชีอื่น
      void clearOutbox();
    }
    return ok;
  }, [qc]);

  const value = useMemo<AuthValue>(() => ({ status, login: startLogin, logout }), [status, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
